import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  listLots,
  lotsCarryLastUsableDay,
  moveCreditExpiry,
  reduceLot,
  type CreditLot,
  type MoveCreditExpiryInput,
  type ReduceLotInput,
  type RenewalCadence,
} from '@/api/creditLots';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { adjustMemberCredits, type AdjustCreditsBatchInput } from '@/api/members';
import { newIdempotencyKey } from '@/api/payments';
import { serverMessage } from '@/api/serverMessage';
import { useToast } from '@/components/useToast';
import { type AdminTokens, Icon, inkAlpha, resolveTokens } from '@/theme';
import { buildMembersMaps, type MembersMaps } from '@/theme/maps/members';
import { formatCredits } from '@/utils/v2Format';
import {
  boundaryInstantForLastUsableDay,
  expiryLabel,
  lastUsableDayOf,
} from '@/utils/creditExpiry';
import { scrubBrand } from '@/utils/whitelabel';
import {
  toCreditAdjustErrorMessage,
  toLotReduceErrorMessage,
  toLotsExpiryErrorMessage,
  MEMBERS_FORBIDDEN_MESSAGE,
  MEMBERS_LOAD_FAILED_MESSAGE,
} from './membersError';
import { ScreenError } from '@/components/ScreenError';
import { todayIn } from '@/components/list/listDate';
import { useCalendarZone } from '@/location/useCalendarZone';
import {
  authRefusalMessage,
  outcomeIsUnknown,
  unconfirmedMessage,
  UNCONFIRMED_CREDITS_CLOSE_PROMPT,
} from './unconfirmedOutcome';

const MIN_REASON = 4;
const ADJUST_REASON = 'Manage credits adjustment';

type WorkspaceState =
  | { status: 'loading' }
  | { status: 'loaded'; lots: CreditLot[] }
  | { status: 'error'; error: unknown }
  | { status: 'reconcile-error'; error: unknown };

/**
 * Where a picked expiry day goes on the wire. `null` = never expires. DNKAPI-0648: when the
 * loaded lots carry the API's plain `lastUsableDay`, the picked day is sent as that plain
 * date (no instant, no zone math); otherwise the legacy exclusive-boundary instant.
 */
type ExpiryTarget = { expiresAt: string } | { lastUsableDay: string };

interface ExpiryGroup {
  key: string;
  expiresAt: string | null;
  /** The API's plain last usable day for the group's lots (DNKAPI-0648), or null (older API / never). */
  lastUsableDay: string | null;
  /** True when the loaded lots carry the day, so writes use plain days. Same for every group. */
  plainDays: boolean;
  lots: CreditLot[];
  total: number;
}

interface RowInput {
  credits: string;
  date: string;
  reason: string;
}

interface AddInput {
  amount: string;
  date: string;
}

const EMPTY_ADD: AddInput = { amount: '', date: '' };

/**
 * DNKADM-0479 (F-244): one write of a save, exactly as sent, under its own key: one lot's
 * share of a reduction, a grant, or the expiry move. A save is a short run of them, in order,
 * so a re-send of one the server already applied is answered without applying it again.
 */
type CreditWrite =
  | { kind: 'reduceLot'; lotId: string; input: ReduceLotInput }
  | { kind: 'adjust'; input: AdjustCreditsBatchInput }
  | { kind: 'move'; input: MoveCreditExpiryInput };

/**
 * DNKADM-0479: a save the server never confirmed either way. `writes` starts with the one that
 * got no answer, then those not yet sent; `reduced` and `committed` are what the save already
 * settled, so the result after "Check again" reads as one save.
 */
interface UnconfirmedSave {
  /** The expiry group's key, or `add` for the add-grant row. */
  target: string;
  /** The group the save was built from (null for the add-grant row). */
  group: ExpiryGroup | null;
  writes: CreditWrite[];
  reduced: number;
  committed: boolean;
}

/** The answer to a run of writes: all applied, or where it stopped and why. */
type WriteRun =
  | { ok: true; returnedLots: CreditLot[] | undefined; committed: boolean }
  | {
      ok: false;
      error: unknown;
      /** The write that failed and every one after it, none of them settled. */
      remaining: [CreditWrite, ...CreditWrite[]];
      reduced: number;
      committed: boolean;
    };

export interface ManageCreditsDialogProps {
  contactId: string;
  bucketId: string;
  /** BACKEND text — brand-scrubbed HERE, at this component's render boundary. */
  bucketName: string;
  onClose: () => void;
  onCreditsChanged: () => void;
}

export function ManageCreditsDialog({
  contactId,
  bucketId,
  bucketName,
  onClose,
  onCreditsChanged,
}: ManageCreditsDialogProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const toast = useToast();
  // The bucket name is BACKEND text, so it is scrubbed INSIDE the component (DNKADM-0249)
  // rather than at one call site: the white-label guard scans source for brand LITERALS
  // and cannot see a brand arriving at runtime, so any future call site would otherwise
  // reintroduce the gap. `scrubBrand` is idempotent over already-clean text, so the
  // existing MemberDetail call-site scrub (which also feeds the action `aria-label`s)
  // stays and simply passes through.
  const safeBucketName = scrubBrand(bucketName) ?? bucketName;
  const titleId = `${useId()}-title`;
  const [state, setState] = useState<WorkspaceState>({ status: 'loading' });
  const [inputs, setInputs] = useState<Record<string, RowInput>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [amountCompleted, setAmountCompleted] = useState<Record<string, boolean>>({});
  const [addInput, setAddInput] = useState<AddInput>(EMPTY_ADD);
  const [applying, setApplying] = useState<string | null>(null);
  /** DNKADM-0479: a save with a write that got no answer; the workspace locks until it settles. */
  const [unconfirmed, setUnconfirmed] = useState<UnconfirmedSave | null>(null);
  const locked = applying !== null || unconfirmed !== null;
  /**
   * A write already started this frame. `locked` is state, so two clicks in the same frame
   * both read it unset (staging 10/28 D7); this ref is set before the first await.
   */
  const inFlightRef = useRef(false);
  /**
   * The Add grant's idempotency key: minted on the first press and reused by every press of
   * the same grant, so twin clicks dedupe at dink-api. Cleared when the amount or day changes
   * (dink-api dedupes on the key alone and would answer a changed grant under a used key
   * with nothing granted) and once the grant is applied, so the next grant gets a new key.
   * The dialog mounts per open, so reopening starts with none.
   */
  const addKeyRef = useRef<string | null>(null);
  // Lot days are on the location calendar (DNKADM-0439, Operator ruling 2026-10-15).
  const zone = useCalendarZone();

  const load = useCallback(() => {
    setState({ status: 'loading' });
    let cancelled = false;
    listLots(contactId, bucketId)
      .then((breakdown) => {
        if (!cancelled) setState({ status: 'loaded', lots: activeLots(breakdown.lots) });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ status: 'error', error });
      });
    return () => {
      cancelled = true;
    };
  }, [bucketId, contactId]);

  useEffect(load, [load]);

  const groups = useMemo(
    () => (state.status === 'loaded' ? groupByExactExpiry(state.lots) : []),
    [state],
  );
  const lots = state.status === 'loaded' ? activeLots(state.lots) : [];
  const total = lots.reduce((sum, lot) => sum + lot.remaining, 0);

  function updateInput(key: string, group: ExpiryGroup, patch: Partial<RowInput>) {
    if (amountCompleted[key]) return;
    setInputs((current) => ({
      ...current,
      [key]: { ...(current[key] ?? defaultInput(group, zone)), ...patch },
    }));
    setErrors((current) => withoutKey(current, key));
  }

  async function reconcile(returnedLots?: CreditLot[]): Promise<void> {
    if (returnedLots !== undefined) {
      setState({ status: 'loaded', lots: activeLots(returnedLots) });
      return;
    }
    setState({ status: 'loading' });
    const breakdown = await listLots(contactId, bucketId);
    setState({ status: 'loaded', lots: activeLots(breakdown.lots) });
  }

  async function reconcileCommitted(key: string, message: string): Promise<void> {
    onCreditsChanged();
    try {
      await reconcile();
      setErrors((current) => ({ ...current, [key]: message }));
    } catch (reconcileError) {
      setState({ status: 'reconcile-error', error: reconcileError });
    }
    setApplying(null);
  }

  function close() {
    if (applying !== null) return;
    if (unconfirmed !== null) {
      // DNKADM-0479: a credit change that may still land is never left silently.
      if (!window.confirm(UNCONFIRMED_CREDITS_CLOSE_PROMPT)) return;
      onCreditsChanged();
    }
    onClose();
  }

  /**
   * Settle a run of writes: success, a lock on an unknown outcome (DNKADM-0479), or the
   * refusal's message. `group` is null for the add-grant row.
   */
  async function settleSave(target: string, group: ExpiryGroup | null, run: WriteRun) {
    if (!run.ok) {
      const [failed] = run.remaining;
      // The expiry move runs last, so a committed save stopped there has its amount saved.
      if (group !== null && failed.kind === 'move' && run.committed) {
        setAmountCompleted((current) => ({ ...current, [group.key]: true }));
      }
      if (outcomeIsUnknown(run.error, unconfirmed !== null)) {
        setUnconfirmed({
          target,
          group,
          writes: run.remaining,
          reduced: run.reduced,
          committed: run.committed,
        });
        // DNKADM-0480: a re-send refused by auth keeps the lock and says why.
        const authLine = authRefusalMessage(run.error);
        if (authLine !== null) setErrors((current) => ({ ...current, [target]: authLine }));
        setApplying(null);
        return;
      }
      setUnconfirmed(null);
      if (group === null) {
        setErrors((current) => ({ ...current, add: toCreditAdjustErrorMessage(run.error) }));
        setApplying(null);
        return;
      }
      if (failed.kind === 'reduceLot' && run.reduced > 0) {
        await reconcileCommitted(
          group.key,
          `Credits were partially reduced. Retry Save to apply only the remaining difference. ${toLotReduceErrorMessage(run.error, group.total)}`,
        );
        return;
      }
      if (run.committed) {
        await reconcileCommitted(
          group.key,
          `Credits changed, but the expiry change failed. Retry Save to finish without applying the amount again. ${toLotsExpiryErrorMessage(run.error)}`,
        );
        return;
      }
      const message =
        failed.kind === 'reduceLot'
          ? toLotReduceErrorMessage(run.error, group.total)
          : failed.kind === 'adjust'
            ? toCreditAdjustErrorMessage(run.error)
            : toLotsExpiryErrorMessage(run.error);
      setErrors((current) => ({ ...current, [group.key]: message }));
      setApplying(null);
      return;
    }

    setUnconfirmed(null);
    if (group === null) {
      // The grant is applied: its key is spent, and the next grant gets a new one.
      addKeyRef.current = null;
      onCreditsChanged();
      try {
        await reconcile();
        setAddInput(EMPTY_ADD);
        setErrors({});
        toast('Grant added.');
      } catch (error) {
        setState({ status: 'reconcile-error', error });
      } finally {
        setApplying(null);
      }
      return;
    }
    try {
      if (run.committed) onCreditsChanged();
      await reconcile(run.returnedLots);
      setInputs((current) => withoutKey(current, group.key));
      setAmountCompleted((current) => withoutKey(current, group.key));
      setErrors({});
      toast('Credits updated.');
    } catch (error) {
      setState({ status: 'reconcile-error', error });
    } finally {
      setApplying(null);
    }
  }

  /** DNKADM-0479: settle an unconfirmed save by re-sending its writes under their own keys. */
  async function checkAgain() {
    if (unconfirmed === null || applying !== null) return;
    const save = unconfirmed;
    await exclusive(async () => {
      setApplying(save.target);
      setErrors((current) => withoutKey(current, save.target));
      const run = await sendWrites(contactId, bucketId, save.writes, save.reduced, save.committed);
      await settleSave(save.target, save.group, run);
    });
  }

  /** Run one write sequence at a time, including against a twin click in the same frame. */
  async function exclusive(run: () => Promise<void>): Promise<void> {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      await run();
    } finally {
      inFlightRef.current = false;
    }
  }

  async function applyRow(group: ExpiryGroup) {
    const input = inputs[group.key] ?? defaultInput(group, zone);
    const targetCredits = parsedCredits(input.credits);
    const target = pickedTarget(input, groups, zone);
    const amountWasCompleted = amountCompleted[group.key] === true;
    const amountChanged =
      !amountWasCompleted && targetCredits !== null && targetCredits !== group.total;
    const expiryChanged = target !== undefined && !targetMatchesGroup(group, target, zone);
    const reductionAmount =
      targetCredits !== null && targetCredits < group.total ? group.total - targetCredits : null;
    const reason = input.reason.trim();

    if (
      locked ||
      targetCredits === null ||
      target === undefined ||
      (!amountChanged && !expiryChanged && !amountWasCompleted) ||
      (targetCredits !== null && !isHalfStep(targetCredits)) ||
      (reductionAmount !== null && reason.length < MIN_REASON)
    ) {
      return;
    }

    // DNKADM-0479: the save's writes, each under a key of its own, built once from the
    // group as loaded so "Check again" re-sends exactly these.
    const writes: CreditWrite[] = [];
    if (amountChanged) {
      if (reductionAmount !== null) {
        for (const peel of planGroupReduction(group, reductionAmount)) {
          writes.push({
            kind: 'reduceLot',
            lotId: peel.lotId,
            input: { amount: peel.amount, reason, idempotencyKey: newIdempotencyKey() },
          });
        }
      } else {
        writes.push({
          kind: 'adjust',
          input: {
            reason: ADJUST_REASON,
            adjustments: [
              {
                creditBucketId: bucketId,
                amount: targetCredits - group.total,
                ...(target === null ? {} : target),
              },
            ],
            idempotencyKey: newIdempotencyKey(),
          },
        });
      }
    }
    if (expiryChanged && targetCredits > 0) {
      writes.push({
        kind: 'move',
        input: {
          fromExpiresAt: group.expiresAt,
          ...moveTarget(target),
          idempotencyKey: newIdempotencyKey(),
        },
      });
    }

    await exclusive(async () => {
      setApplying(group.key);
      setErrors((current) => withoutKey(current, group.key));
      const run = await sendWrites(contactId, bucketId, writes, 0, amountWasCompleted);
      await settleSave(group.key, group, run);
    });
  }

  async function addGrant() {
    const amount = parsedPositiveAmount(addInput.amount);
    const target = pickedTarget({ date: addInput.date }, groups, zone);
    if (locked || amount === null || !isHalfStep(amount) || target === undefined) return;

    const idempotencyKey = addKeyRef.current ?? newIdempotencyKey();
    addKeyRef.current = idempotencyKey;
    await exclusive(async () => {
      setApplying('add');
      setErrors((current) => withoutKey(current, 'add'));
      const run = await sendWrites(
        contactId,
        bucketId,
        [
          {
            kind: 'adjust',
            input: {
              reason: ADJUST_REASON,
              adjustments: [
                { creditBucketId: bucketId, amount, ...(target === null ? {} : target) },
              ],
              idempotencyKey,
            },
          },
        ],
        0,
        false,
      );
      await settleSave('add', null, run);
    });
  }

  return (
    <Dialog
      open
      onClose={close}
      maxWidth="md"
      fullWidth
      aria-labelledby={titleId}
    >
      <DialogTitle
        id={titleId}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.75 }}
      >
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            bgcolor: t.brandSoft,
            color: t.brand,
            borderRadius: 1,
            width: 38,
            height: 38,
            flexShrink: 0,
          }}
        >
          <Icon name="account_balance_wallet" sx={{ fontSize: 21 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
            Manage credits — {safeBucketName}
          </Typography>
          {state.status === 'loaded' && (
            <Typography variant="body2">
              {formatCredits(total)} credits · {lots.length}{' '}
              {lots.length === 1 ? 'grant' : 'grants'}
            </Typography>
          )}
        </Box>
        <IconAction
          size="small"
          onClick={close}
          disabled={applying !== null}
          label="Close Manage credits workspace"
        >
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent dividers sx={{ maxHeight: '68vh' }}>
        {state.status === 'loading' && <WorkspaceLoading />}
        {state.status === 'error' && <WorkspaceError error={state.error} onRetry={load} />}
        {state.status === 'reconcile-error' && (
          <ReconcileError error={state.error} onRetry={load} />
        )}
        {state.status === 'loaded' && (
          <Stack sx={{ gap: 1.25 }}>
            <Box
              sx={{
                display: { xs: 'none', md: 'grid' },
                gridTemplateColumns:
                  'minmax(170px, 1.2fr) minmax(135px, .75fr) minmax(210px, 1fr) auto',
                gap: 1.25,
                px: 2,
              }}
            >
              {['GRANT', 'CREDITS', 'EXPIRES', ''].map((label, index) => (
                <Typography
                  key={`${label}-${index}`}
                  sx={{ fontSize: 11, fontWeight: 700, color: t.textSubtle }}
                >
                  {label}
                </Typography>
              ))}
            </Box>
            {groups.map((group) => (
              <CreditGroupRow
                key={group.key}
                group={group}
                groups={groups}
                input={inputs[group.key] ?? defaultInput(group, zone)}
                error={errors[group.key]}
                disabled={locked}
                amountCompleted={amountCompleted[group.key] === true}
                applying={applying === group.key}
                onChange={(patch) => updateInput(group.key, group, patch)}
                onSave={() => void applyRow(group)}
              />
            ))}
            {groups.length === 0 && (
              <Typography variant="body2" sx={{ py: 2 }}>
                No active credits in this bucket. Add a grant below to get started.
              </Typography>
            )}
            <AddGrantRow
              input={addInput}
              error={errors['add']}
              disabled={locked}
              applying={applying === 'add'}
              plainDays={groups.some((group) => group.plainDays)}
              onChange={(patch) => {
                addKeyRef.current = null;
                setAddInput((current) => ({ ...current, ...patch }));
                setErrors((current) => withoutKey(current, 'add'));
              }}
              onAdd={() => void addGrant()}
            />
          </Stack>
        )}
      </DialogContent>

      {/* DNKADM-0479: pinned outside the scrolling workspace, so the lock is seen
          without scrolling. */}
      {unconfirmed !== null && (
        <Box sx={{ px: 3, pt: 1.5, flexShrink: 0 }}>
          <Alert
            severity="warning"
            role="alert"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void checkAgain()}
                disabled={applying !== null}
              >
                Check again
              </Button>
            }
          >
            {unconfirmedMessage('credits')}
          </Alert>
        </Box>
      )}

      <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ color: t.text2 }}>
          Each save applies immediately
        </Typography>
        <Button color="inherit" onClick={close} disabled={applying !== null}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CreditGroupRow({
  group,
  groups,
  input,
  error,
  disabled,
  amountCompleted,
  applying,
  onChange,
  onSave,
}: {
  group: ExpiryGroup;
  groups: ExpiryGroup[];
  input: RowInput;
  error: string | undefined;
  disabled: boolean;
  amountCompleted: boolean;
  applying: boolean;
  onChange: (patch: Partial<RowInput>) => void;
  onSave: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const members = buildMembersMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
  const zone = useCalendarZone();
  const label = expiryLabel(group.expiresAt, zone, group.lastUsableDay);
  const credits = parsedCredits(input.credits);
  const target = pickedTarget(input, groups, zone);
  const amountChanged = !amountCompleted && credits !== null && credits !== group.total;
  const reducing = !amountCompleted && credits !== null && credits < group.total;
  const halfStepInvalid = credits !== null && !isHalfStep(credits);
  const expiryChanged = target !== undefined && !targetMatchesGroup(group, target, zone);
  const reasonTouched = input.reason.length > 0;
  const reasonValid = input.reason.trim().length >= MIN_REASON;
  const dateInvalid = input.date !== '' && !isFutureDate(input.date, zone, group.plainDays);
  const canSave =
    !disabled &&
    credits !== null &&
    target !== undefined &&
    (amountChanged || expiryChanged || amountCompleted) &&
    !halfStepInvalid &&
    (!reducing || reasonValid);

  return (
    <Box
      component="section"
      role="region"
      aria-label={`Expiry group ${label}`}
      sx={workspaceSectionSx(t, members)}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(170px, 1.2fr) minmax(135px, .75fr) minmax(210px, 1fr) auto',
          },
          gap: 1.25,
          alignItems: 'start',
        }}
      >
        <Stack sx={{ gap: 0.35, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{label}</Typography>
          {group.lots.map((lot) => (
            <Typography key={lot.lotId} sx={{ fontSize: 12, color: inkAlpha(theme, 0.62) }}>
              {grantLabel(lot)} · {formatCredits(lot.remaining)} now
            </Typography>
          ))}
        </Stack>
        <TextField
          type="number"
          size="small"
          label="Credits"
          value={input.credits}
          onChange={(event) => onChange({ credits: event.target.value })}
          disabled={disabled || amountCompleted}
          error={credits === null || halfStepInvalid}
          helperText={
            credits === null
              ? 'Enter zero or a positive number.'
              : halfStepInvalid
                ? 'Use 0.5 credit increments.'
                : 'Absolute group total.'
          }
          slotProps={{ htmlInput: { min: 0, step: 'any', 'aria-label': `Credits for ${label}` } }}
        />
        <Stack sx={{ gap: 0.25 }}>
          <TextField
            type="date"
            size="small"
            label="Good through"
            value={input.date}
            onChange={(event) => onChange({ date: event.target.value })}
            disabled={disabled || amountCompleted}
            error={dateInvalid}
            helperText={dateInvalid ? 'Pick a future date.' : 'Blank means no expiration.'}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { 'aria-label': `Good through for ${label}` },
            }}
          />
        </Stack>
        <Button
          variant="contained"
          size="small"
          onClick={onSave}
          disabled={!canSave}
          aria-label={`Save ${label}`}
          sx={{ mt: { md: 0.5 }, whiteSpace: 'nowrap' }}
        >
          {applying ? 'Saving…' : 'Save'}
        </Button>
      </Box>
      {reducing && (
        <TextField
          size="small"
          label="Reason"
          value={input.reason}
          onChange={(event) => onChange({ reason: event.target.value })}
          disabled={disabled}
          required
          error={reasonTouched && !reasonValid}
          helperText={
            reasonTouched && !reasonValid
              ? `Enter at least ${MIN_REASON} characters.`
              : 'Required for the audit history.'
          }
          slotProps={{ htmlInput: { 'aria-label': `Reason for reducing ${label}` } }}
          sx={{ mt: 1.25, width: { xs: '100%', md: '55%' } }}
        />
      )}
      {amountCompleted && (
        <Typography variant="body2" sx={{ mt: 1, color: inkAlpha(theme, 0.65) }}>
          The amount is already saved. Retry Save to finish the expiry change.
        </Typography>
      )}
      {error && (
        <Alert severity="error" role="alert" sx={{ mt: 1.25 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

function AddGrantRow({
  input,
  error,
  disabled,
  applying,
  plainDays,
  onChange,
  onAdd,
}: {
  input: AddInput;
  error: string | undefined;
  disabled: boolean;
  applying: boolean;
  plainDays: boolean;
  onChange: (patch: Partial<AddInput>) => void;
  onAdd: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const members = buildMembersMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
  const amount = parsedPositiveAmount(input.amount);
  const amountEntered = input.amount.trim() !== '';
  const amountHalfInvalid = amount !== null && !isHalfStep(amount);
  const zone = useCalendarZone();
  const dateValid = input.date === '' || isFutureDate(input.date, zone, plainDays);
  return (
    <Box
      component="section"
      aria-label="Add grant"
      sx={{ ...workspaceSectionSx(t, members), bgcolor: t.surface }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(170px, 1.2fr) minmax(135px, .75fr) minmax(210px, 1fr) auto',
          },
          gap: 1.25,
          alignItems: 'start',
        }}
      >
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, pt: 1 }}>+ Add grant</Typography>
        <TextField
          type="number"
          size="small"
          label="Amount"
          value={input.amount}
          onChange={(event) => onChange({ amount: event.target.value })}
          disabled={disabled}
          error={amountEntered && (amount === null || amountHalfInvalid)}
          helperText={amountHalfInvalid ? 'Use 0.5 credit increments.' : 'Positive credits.'}
          slotProps={{ htmlInput: { min: 0, step: 'any', 'aria-label': 'Add grant amount' } }}
        />
        <TextField
          type="date"
          size="small"
          label="Good through (optional)"
          value={input.date}
          onChange={(event) => onChange({ date: event.target.value })}
          disabled={disabled}
          error={!dateValid}
          helperText={dateValid ? 'Blank means no expiration.' : 'Pick a future date.'}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { 'aria-label': 'Add grant expiry' },
          }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={onAdd}
          disabled={disabled || amount === null || amountHalfInvalid || !dateValid}
          aria-label="Add grant"
          sx={{ mt: { md: 0.5 }, whiteSpace: 'nowrap' }}
        >
          {applying ? 'Adding…' : 'Add'}
        </Button>
      </Box>
      {error && (
        <Alert severity="error" role="alert" sx={{ mt: 1.25 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

function WorkspaceLoading() {
  return (
    <Stack role="status" aria-label="Loading Manage credits workspace" sx={{ gap: 1 }}>
      <Skeleton variant="rounded" height={120} />
      <Skeleton variant="rounded" height={120} />
    </Stack>
  );
}

function WorkspaceError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <ScreenError
      error={error}
      what="the credits"
      onRetry={onRetry}
      forbiddenMessage={MEMBERS_FORBIDDEN_MESSAGE}
      retryMessage={MEMBERS_LOAD_FAILED_MESSAGE}
      preferServerMessage
    />
  );
}

function ReconcileError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <Alert
      severity="error"
      role="alert"
      action={
        <Button color="inherit" size="small" onClick={onRetry}>
          Retry
        </Button>
      }
    >
      The change was saved, but the updated grants could not be loaded. Retry to refresh the
      workspace. {serverMessage(error)}
    </Alert>
  );
}

/** A group reduction as per-lot peels, oldest grant first; each peel is one lot reduce. */
function planGroupReduction(
  group: ExpiryGroup,
  amount: number,
): { lotId: string; amount: number }[] {
  const peels: { lotId: string; amount: number }[] = [];
  let remaining = amount;
  for (const lot of [...group.lots].sort(compareFifoLots)) {
    if (remaining <= 0) break;
    const peel = Math.min(remaining, lot.remaining);
    peels.push({ lotId: lot.lotId, amount: peel });
    remaining -= peel;
  }
  return peels;
}

/**
 * Send a save's writes in order, stopping at the first failure. `reduced` and `committed`
 * carry what an earlier run of the same save already settled.
 */
async function sendWrites(
  contactId: string,
  bucketId: string,
  writes: CreditWrite[],
  reduced: number,
  committed: boolean,
): Promise<WriteRun> {
  let returnedLots: CreditLot[] | undefined;
  for (let i = 0; i < writes.length; i += 1) {
    const write = writes[i]!;
    try {
      if (write.kind === 'reduceLot') {
        await reduceLot(contactId, bucketId, write.lotId, write.input);
        reduced += write.input.amount;
      } else if (write.kind === 'adjust') {
        await adjustMemberCredits(contactId, write.input);
      } else {
        returnedLots = await moveCreditExpiry(contactId, bucketId, write.input);
      }
      committed = true;
    } catch (error) {
      return { ok: false, error, remaining: [write, ...writes.slice(i + 1)], reduced, committed };
    }
  }
  return { ok: true, returnedLots, committed };
}

function compareFifoLots(left: CreditLot, right: CreditLot): number {
  const leftTime = left.grantedAt === null ? Number.MAX_SAFE_INTEGER : Date.parse(left.grantedAt);
  const rightTime =
    right.grantedAt === null ? Number.MAX_SAFE_INTEGER : Date.parse(right.grantedAt);
  const timeDiff = safeSortTime(leftTime) - safeSortTime(rightTime);
  return timeDiff !== 0 ? timeDiff : left.lotId.localeCompare(right.lotId);
}

function safeSortTime(value: number): number {
  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
}

function activeLots(lots: CreditLot[]): CreditLot[] {
  return lots.filter((lot) => lot.remaining > 0);
}

function groupByExactExpiry(lots: CreditLot[]): ExpiryGroup[] {
  const plainDays = lotsCarryLastUsableDay(activeLots(lots));
  const grouped = new Map<string, CreditLot[]>();
  for (const lot of activeLots(lots)) {
    const key = lot.expiresAt === null ? 'never' : lot.expiresAt;
    grouped.set(key, [...(grouped.get(key) ?? []), lot]);
  }
  return [...grouped.entries()]
    .map(([key, groupLots]) => ({
      key,
      expiresAt: key === 'never' ? null : key,
      lastUsableDay: groupLots.find((lot) => lot.lastUsableDay !== null)?.lastUsableDay ?? null,
      plainDays,
      lots: [...groupLots].sort(compareFifoLots),
      total: groupLots.reduce((sum, lot) => sum + lot.remaining, 0),
    }))
    .sort((left, right) => {
      if (left.expiresAt === null) return 1;
      if (right.expiresAt === null) return -1;
      return expirySortValue(left.expiresAt) - expirySortValue(right.expiresAt);
    });
}

function defaultInput(group: ExpiryGroup, zone: string | null): RowInput {
  return {
    credits: String(group.total),
    // The picker means "last usable day" (DNKADM-0306), so seed it with the
    // day BEFORE the stored exclusive boundary — the inverse of the write path.
    date: lastUsableDayOf(group.expiresAt, zone, group.lastUsableDay) ?? '',
    reason: '',
  };
}

function parsedCredits(value: string): number | null {
  if (value.trim() === '') return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}

function parsedPositiveAmount(value: string): number | null {
  const amount = parsedCredits(value);
  return amount !== null && amount > 0 ? amount : null;
}

const HALF_STEP_EPSILON = 1e-6;

function isHalfStep(value: number): boolean {
  if (!Number.isFinite(value) || value < 0) return false;
  const doubled = value * 2;
  return Math.abs(doubled - Math.round(doubled)) < HALF_STEP_EPSILON;
}

function pickedTarget(
  input: Pick<RowInput, 'date'>,
  groups: ExpiryGroup[],
  zone: string | null,
): ExpiryTarget | null | undefined {
  if (input.date === '') return null;
  const plainDays = groups.some((group) => group.plainDays);
  if (!isFutureDate(input.date, zone, plainDays)) return undefined;
  // DNKAPI-0648: the API sent plain days, so the picked day goes back as a plain day. No
  // instant, no location-zone midnight, no UTC-midnight fallback on this path.
  if (plainDays) return { lastUsableDay: input.date };
  // Older API: the picked day is the LAST USABLE day; the wire carries the exclusive
  // boundary (the start of picked + 1 in the calendar zone, DNKADM-0306/0439).
  const pickedInstant = boundaryInstantForLastUsableDay(input.date, zone);
  return {
    expiresAt:
      groups.find(
        (group) =>
          group.expiresAt !== null && expiryCalendarDaysEqual(group.expiresAt, pickedInstant, zone),
      )?.expiresAt ?? pickedInstant,
  };
}

/** The expiry-move body fields for a target: never, an instant, or a plain day. */
function moveTarget(
  target: ExpiryTarget | null | undefined,
): { toExpiresAt?: string | null; toLastUsableDay?: string } {
  // Only an explicit null means "never expires". An undefined target sends nothing, so the
  // move's own validation fires ("Choose a target expiry") rather than making credits permanent.
  if (target === undefined) return {};
  if (target === null) return { toExpiresAt: null };
  return 'lastUsableDay' in target
    ? { toLastUsableDay: target.lastUsableDay }
    : { toExpiresAt: target.expiresAt };
}

/** Is the target the group's own expiry day? The group's day is the API's when it sent one. */
function targetMatchesGroup(
  group: ExpiryGroup,
  target: ExpiryTarget | null,
  zone: string | null,
): boolean {
  if (target === null) return group.expiresAt === null;
  if (group.expiresAt === null) return false;
  if ('lastUsableDay' in target) {
    return lastUsableDayOf(group.expiresAt, zone, group.lastUsableDay) === target.lastUsableDay;
  }
  return expiryCalendarDaysEqual(group.expiresAt, target.expiresAt, zone);
}

/** Same last usable day in `zone` (so a picked day lands on an existing group's exact instant). */
function expiryCalendarDaysEqual(left: string | null, right: string | null, zone: string | null): boolean {
  if (left === null || right === null) return left === right;
  return lastUsableDayOf(left, zone, null) === lastUsableDayOf(right, zone, null);
}

function isFutureDate(dateValue: string, zone: string | null, plainDays: boolean): boolean {
  // Plain-day path: no instant to compare, but the same guard as the instant path: any day
  // before the calendar zone's today is refused (dink-api has no past check on grants or the
  // lot expiry write, and a past day expires the lot).
  if (plainDays) return dateValue >= todayIn(zone);
  // The picked day is the last usable day, so validate its exclusive BOUNDARY
  // (the start of picked + 1 in the calendar zone) is still ahead — today is a
  // valid choice; the old picked-midnight check rejected it (DNKADM-0306).
  const boundary = Date.parse(boundaryInstantForLastUsableDay(dateValue, zone));
  return Number.isFinite(boundary) && boundary > Date.now();
}

function expirySortValue(expiresAt: string): number {
  const parsed = Date.parse(expiresAt);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

function grantLabel(lot: CreditLot): string {
  switch (lot.grantOrigin) {
    case 'PLAN_REFILL':
      return lot.planName ?? cadencePlanLabel(lot.renewalCadence);
    case 'PLAN_ADDON':
      return lot.planName ? `Add-on · ${lot.planName}` : 'Add-on';
    case 'PURCHASE':
      return 'Purchased';
    case 'IMPORT':
      return 'Imported';
    case 'ADMIN':
      return 'Adjustment';
    default:
      return sourceFallbackLabel(lot.source);
  }
}

function cadencePlanLabel(cadence: RenewalCadence | null): string {
  switch (cadence) {
    case 'WEEKLY':
      return 'Weekly plan';
    case 'MONTHLY':
      return 'Monthly plan';
    case 'QUARTERLY':
      return 'Quarterly plan';
    case 'ANNUAL':
      return 'Annual plan';
    default:
      return 'Plan';
  }
}

function sourceFallbackLabel(source: string | null): string {
  switch (source?.trim().toUpperCase()) {
    case 'PURCHASE':
      return 'Purchased';
    case 'PACKAGE':
      return 'Promo / Package';
    case 'REFILL':
      return 'Membership renewal';
    case 'ADMIN':
      return 'Adjustment';
    case 'IMPORT':
      return 'Imported';
    default:
      return 'Granted';
  }
}

function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  const { [key]: _removed, ...rest } = record;
  return rest;
}

function workspaceSectionSx(t: AdminTokens, members: MembersMaps) {
  return {
    p: '14px 16px',
    borderRadius: 2,
    border: `1px solid ${t.line}`,
    bgcolor: members.surfaces.workspace,
  };
}
